import React, { useEffect, useCallback, useState } from 'react';
import classnames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import * as BreakdownSelectors from 'store/modules/bookingManager/subdomains/breakdown/selectors';
import * as BreakdownActions from 'store/modules/bookingManager/subdomains/breakdown/actions';
import styled from 'styled-components';
import { generateArrayOfDatesBetween, getOverlappingDates, isBlank } from 'utils';
import {
  BookingSearchItem,
  EBookingStatus,
  ERateSource,
  IHeadlineLineItemBreakdownAccommodationLineItem,
} from 'services/BookingManagerApi';
import { PriceAsCentsInput } from '../stateful/PriceAsCentsInput';
import { ProductsBlock } from './ProductsBlock';
import InfoIcon from '../Icons/Info.component.svg';
import { theme } from '../../../tailwind.config';
import { HidingTooltip } from '../Tooltip';
import { ENetworkRequestStatus } from 'services/BackendApi';
import FluidButton from 'ui/FluidButton';
import { DatePickerStateProvider, IDatePickerSateParams } from 'pureUi/providers/DatePickerStateProvider';
import DateRangeInput from 'pureUi/DateRangeInput';
import { SvgIcon } from 'ui/SvgIcon';
import CloseIcon from 'ui/Icons/close.component.svg';
import ChevronDownIcon from 'ui/Icons/tabler/chevron-down.component.svg';
import ChevronUpIcon from 'ui/Icons/tabler/chevron-up.component.svg';
import { isNilOrEmpty } from 'ramda-adjunct';
import { RemoveHeadlineButton, EditHeadlineButton } from './HeadlineActionButtons';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';
import { isNil } from 'lodash-es';
import { hasOverlap } from 'utils/array';
import { hotelDetailsSelector } from 'store/modules/bookingManager/subdomains/hotelDetails/selectors';
import { ProductTypes } from 'config/enums';
import { useBookLiveAccommodation } from './useBookLiveAccommodation';
import { AccommodationBlockBookLiveModal } from './AccommodationBlockBookLiveModal';
import * as HotelDetailsActions from 'store/modules/bookingManager/subdomains/hotelDetails/actions';
import { SubtotalsRow } from './SubtotalsRow';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import classNames from 'classnames';
import { AllocationMatrixGuest } from 'services/BookingManagerApi/types/AllocationsResponse';
import { BreakdownAllocationTooltipContent } from './BreakdownAllocationTooltipContent';
import { PeopleComponent } from 'ui/Icons';
import * as DateUtc from 'utils/date.utc';

interface IAccommodationBlockProps {
  key: string;
  index: number;
  accom: IHeadlineLineItemBreakdownAccommodationLineItem;
  bookingCurrencySymbol: string;
  bookingStatus: string;
  setHeadlineLineItemFieldAction: any;
  removeHeadlineLineItemAction: any;
  addHeadlineLineItemAction: any;
  setBreakdownHoldAction: any;
  isEditable: boolean;
  isPurchaseCostEditable: boolean;
  isSR: boolean;
  isTA: boolean;
  isAdmin: boolean;
  isFinanceUser: boolean;
  isFinanceWithCompletedStatus: boolean;
  isSales: boolean;
  isLiveRate: boolean;
  isLatestVersion: boolean;
  allAccommodationItems: IHeadlineLineItemBreakdownAccommodationLineItem[];
  bookingUuid: string;
  bookingSearchItem: BookingSearchItem | null;
  isOldVersion?: boolean;
  allocationGuests: AllocationMatrixGuest[];
  isGuestAllocationEnabled: boolean;
  forceShowEditIcon?: boolean;
}

// necessary because we dont have placeholder utility classes
const AccommodationTopInput = styled.input`
  &::placeholder {
    font-weight: normal;
    font-size: 16px;
    font-style: italic;
    color: #968f8b; // gray-100
  }
`;

// necessary because we need it to be a different border colour than normal
// and the date range input is a super nested styled component
// @ts-ignore ts getting confused by `styled` props
const StyledDateRangeInput = styled(DateRangeInput)`
  .pseudoSelect {
    border-color: #d5d2d1; //gray-40
  }
`;

export const AccommodationBlock = (props: IAccommodationBlockProps) => {
  const dispatch = useDispatch();

  const paymentTerms = useSelector(BreakdownSelectors.paymentTermsSelector);
  const cancellationPolicies = useSelector(BreakdownSelectors.cancellationPoliciesSelector);
  const policiesAndRestrictions = useSelector(BreakdownSelectors.policiesAndRestrictionsSelector);
  const offerTerms = useSelector(BreakdownSelectors.offerTermsSelector);
  const headlineLineItemBreakdown = useSelector(BreakdownSelectors.headlineLineItemBreakdownSelector);

  // orchestrator for booking a live accommodation
  const bookLiveAccommodationOps = useBookLiveAccommodation({
    accom: props.accom,
    bookingUuid: props.bookingUuid,
    travelAgentUuid: props.bookingSearchItem?.travelAgentUuid,
    currencySymbol: props.bookingSearchItem?.currencySymbol,
    accommodationIndex: props.index,
    paymentTerms: paymentTerms,
    cancellationPolicies: cancellationPolicies,
    policiesAndRestrictions: policiesAndRestrictions,
    offerTerms: offerTerms,
    headlineLineItemBreakdown: headlineLineItemBreakdown,
  });

  const { dynamicParameters } = useDynamicParameters();
  const accom: IHeadlineLineItemBreakdownAccommodationLineItem = props.accom;
  const index = props.index;
  const uuid = props.accom.uuid;
  const bookingStatus = props.bookingStatus;
  const enabledAccomInfo =
    dynamicParameters.ENABLE_EDIT_ACCOMM_MODALS_TEMP && !isNil(accom.requestMeta) && accom.enableEditAccommodationModal;
  const isInternalUser = props.isSR || props.isAdmin || props.isFinanceUser;
  const showEditIcon = props.forceShowEditIcon || (enabledAccomInfo && !props.isLiveRate && isInternalUser && props.isLatestVersion);

  /**
   * We can delete an accommodation if:
   *   you're an internal user AND
   *   more than 1 total accoms AND
   *     the accommodation is NOT a live rate OR
   *     the accommodation IS a live rate AND the rate source is supplier unconfirmed
   */
  const showRemoveIcon =
    isInternalUser &&
    props.isLatestVersion &&
    props.allAccommodationItems.length >= 2 &&
    (!props.isLiveRate || (props.isLiveRate && accom.roomRateSource === ERateSource.SUPPLIER_UNCONFIRMED));

  const isEditable =
    !enabledAccomInfo && !props.isLiveRate && isInternalUser && !bookLiveAccommodationOps.modalOps.isOpen;

  const isSubProductsSalesCostEditable = props.isAdmin || props.isSR || props.isFinanceWithCompletedStatus;
  const subProductsAreEditable = !enabledAccomInfo && !props.isLiveRate && isInternalUser && props.isLatestVersion;
  const isPurchaseCostDisabled = !props.isPurchaseCostEditable || props.isTA;
  const purchaseCostSubtotalCents =
    (accom.purchaseCostCents ?? 0) +
    (accom['Meal Plan'].purchaseCostSubtotalCents ?? 0) +
    (accom.Supplement.purchaseCostSubtotalCents ?? 0) +
    (accom.AdditionalHotelCharges.purchaseCostSubtotalCents ?? 0);
  const saleCostSubtotalCents =
    (accom.saleCostCentsUnreactive ?? 0) +
    (accom['Meal Plan'].subtotalCents ?? 0) +
    (accom.Supplement.subtotalCents ?? 0) +
    (accom.AdditionalHotelCharges.subtotalCents ?? 0);

  let isSalesCostEditable = props.isEditable;
  if (props.isLiveRate) {
    isSalesCostEditable = isSalesCostEditable && (props.isAdmin || props.isSR || props.isFinanceWithCompletedStatus);
  }

  const networkData = useSelector(BreakdownSelectors.breakdownNetworkRequestsSelector);
  const hotelDetails = useSelector(hotelDetailsSelector);

  const liveAccommodationCancellationPolicies =
    useSelector(BreakdownSelectors.liveAccommodationCancellationPoliciesSelector).find(
      x => x.bookingRef === accom.externalBooking?.bookingReference
    )?.cancellationPolicies || [];

  const isPurchaseCostVisible = props.isSR || props.isFinanceUser;

  const [isCancellationPoliciesOpen, setIsCancellationPoliciesOpen] = useState(false);
  const [isCalendarDatesEdited, setIsCalendarDatesEdited] = useState(true);

  const allOverlappingDates = getOverlappingDates(
    props.allAccommodationItems
      .filter(item => !isBlank(item.arrival) && !isBlank(item.departure))
      .map(item => {
        return {
          start: item.arrival,
          end: item.departure,
        };
      })
  );

  const thisAccomDateRange =
    !isBlank(accom.arrival) && !isBlank(accom.departure)
      ? generateArrayOfDatesBetween(accom.arrival!, accom.departure!)
      : [];

  const hasOverlappingDates = hasOverlap(thisAccomDateRange, allOverlappingDates);
  const [hasDismissedOverlapWarning, setHasDismissedOverlapWarning] = useState(false);

  const handleOpenEditAccommodationModal = useCallback(() => {
    dispatch(
      BreakdownActions.editAccommodationOpenModalAction(
        {
          hotelUuids: hotelDetails.map(hd => hd.uuid),
          hotelNames: hotelDetails.map(hd => hd.name),
          roomName: accom.title,
          headlineLineItemBreakdownAccommodationLineItem: accom,
        },
        props.bookingUuid,
        accom.uuid
      )
    );
  }, [accom]);

  // is editable changes when the breakdown is saved and the parent re-renders.
  // so, on an iseditable change - hide the calendar disclaimer
  useEffect(() => {
    setIsCalendarDatesEdited(false);
  }, [isEditable]);

  const typologyInfoTooltipContent = useCallback(() => {
    return (
      <div style={{ width: '116px' }} className="font-pt-sans text-13px p-2">
        {accom.externalBooking?.status === 'confirmed' && (
          <span>Unique Booking Identification provided by the supplier.</span>
        )}
        {accom.externalBooking?.status === 'cancelled' && (
          <span>Cancelllation ID provided by the supplier. Cancel costs applied.</span>
        )}
      </div>
    );
  }, [accom]);

  const shouldShowExternalBookingDetails =
    accom.roomRateSource === ERateSource.SUPPLIER_CONFIRMED &&
    !!accom.externalBooking?.status &&
    !!accom.externalBooking?.hotelBookingId;

  const showRightButtonGroup =
    showEditIcon || showRemoveIcon || (props.isLatestVersion && accom.roomRateSource === ERateSource.SUPPLIER_UNCONFIRMED);

  return (
    <React.Fragment>
      <div className="breakdown-accommodation-product mb-[10px] w-full">
        <div className="pt-[10px] pb-[10px]">
          {/* row 1 - the accommodation index, conf number, book button, etc. */}
          <div
            className={classNames('grid gap-20px items-center mb-20px', {
              'grid-cols-[1fr_1fr_24px]': !props.isTA,
              'grid-cols-[1fr_1fr]': props.isTA,
            })}
          >
            <div className="justify-self-start">
              {/* accom index and guest allocation tooltip (if enabled) */}
              <span className="flex items-center space-x-2">
                <span className="font-pt-sans font-bold text-17px leading-22px text-black-true">
                  Accommodation {index + 1}
                </span>
                {props.isGuestAllocationEnabled && (
                  <HidingTextTooltip
                    tooltipContent={<BreakdownAllocationTooltipContent guests={props.allocationGuests || []} />}
                    position="bottom-direct"
                    elementClassname="justify-start gap-5px w-fit"
                    tooltipContentClassname="py-[5px] px-[8px]"
                    tooltipWrapperClassname="w-fit"
                    keepTooltipOnHover
                  >
                    <PeopleComponent className="bg-brown-prime-10 px-[2.5px] py-[3px] rounded-3px" />
                  </HidingTextTooltip>
                )}
              </span>

              {/* the badges re. rate */}
              <div className="flex space-x-2 mt-10px">
                {accom.roomRateSource === ERateSource.SUPPLIER_CONFIRMED && (
                  <span className="text-xs accommodation-rate bg-teal-40 border border-solid border-teal-80 px-5px py-2px uppercase self-center font-hurmegeometric-sans rounded-1px text-black-true">
                    Live Rate
                  </span>
                )}
                {accom.roomRateSource === ERateSource.SUPPLIER_UNCONFIRMED && (
                  <span className="text-xs bg-yellow-20 border border-solid border-yellow-30 px-5px py-2px uppercase font-hurmegeometric-sans rounded-1px text-black mr-2">
                    Live Rate
                  </span>
                )}
                {(accom.roomRateSource === ERateSource.STATIC || !accom.roomRateSource) && (
                  <span className="text-xs accommodation-rate border border-solid border-gray-50 px-5px py-2px uppercase self-center font-hurmegeometric-sans rounded-1px text-black-true">
                    Static Rate
                  </span>
                )}
                {accom.wasCreatedInstantBook && (
                  <span className="text-xs accommodation-rate border-solid bg-teal-40 border border-teal-80 px-5px py-2px uppercase self-center font-hurmegeometric-sans rounded-1px text-black-true">
                    Instant Book
                  </span>
                )}
              </div>
            </div>

            <div className="justify-self-end">
              <div className="flex items-center gap-20px">
                {/* confirm number input */}
                <input
                  placeholder={'Confirmation N.'}
                  value={props.accom.roomConfirmationNumber || ''}
                  type="text"
                  readOnly={!props.isEditable}
                  className={classnames(
                    'w-[130px] resort-confirmation-number-input focus:outline-gray-80 text-black name border text-base border-solid border-gray-40 p-2 font-pt-sans max-h-[35px]',
                    {
                      'bg-gray-10': props.isEditable === false,
                    }
                  )}
                  onChange={event => {
                    props.setHeadlineLineItemFieldAction(
                      `Accommodation.items[${uuid}].roomConfirmationNumber`,
                      event.target.value
                    );
                    dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                  }}
                />

                {shouldShowExternalBookingDetails && (
                  <div className="inline-flex items-center gap-[10px]">
                    <HidingTooltip renderTooltipContent={typologyInfoTooltipContent} position="left-bottom">
                      <span className="flex cursor-pointer typology-info-icon">
                        <InfoIcon width="17px" height="17px" fill={theme.colors['gray-80']} />
                      </span>
                    </HidingTooltip>
                    <span className="external-booking-id flex-shrink-0 text-sm font-normal text-black-true tracking-2xs">
                      {'ID. #'}
                      {accom.externalBooking?.status === 'confirmed'
                        ? accom.externalBooking?.hotelBookingId
                        : accom.externalBooking?.cancellation?.id}
                    </span>

                    <span
                      className={classnames(
                        'text-15px rate-source-supplier-confirmed border border-solid px-5px py-2px uppercase font-hurmegeometric-sans text-black-true',
                        {
                          'bg-green-25 border-teal-80': accom.externalBooking?.status === 'confirmed',
                          'bg-gray-20 border-gray-60': accom.externalBooking?.status === 'cancelled',
                        }
                      )}
                    >
                      {accom.externalBooking?.status}
                    </span>
                  </div>
                )}

                {/* if rate source is supplier unconfirmed, show book now button */}
                {props.isLatestVersion && accom.roomRateSource === ERateSource.SUPPLIER_UNCONFIRMED && (
                  <FluidButton
                    className="min-w-[100px]"
                    isLoading={bookLiveAccommodationOps.modalOps.isOpen}
                    type="primary"
                    onClick={() => {
                      bookLiveAccommodationOps.onBookNow();
                    }}
                  >
                    Book now
                  </FluidButton>
                )}

                {/* if we can edit, show the edit button */}
                {showEditIcon && (
                  <div className="edit-accommodation">
                    <span className="block">
                      <EditHeadlineButton
                        action={handleOpenEditAccommodationModal}
                        pathPrefix={'Accommodation'}
                        productType={''}
                        index={index}
                      />
                    </span>
                  </div>
                )}
              </div>
            </div>

            {showRemoveIcon && (
              <RemoveHeadlineButton
                action={(removePath: string, removeIndex: number) => {
                  props.removeHeadlineLineItemAction(removePath, removeIndex, accom.uuid);

                  // work out which hotel UUIDs are remaining, and give that to the hotel
                  // details reducer, so that it can remove any hotels that are no longer relevant
                  const remainingHotelUuids = props.allAccommodationItems
                    .filter(a => a.uuid !== accom.uuid)
                    .map(a => a.hotelUuid);
                  dispatch(HotelDetailsActions.handleRemoveAccommodationAction(remainingHotelUuids));
                }}
                pathPrefix={'Accommodation'}
                productType={''}
                index={index}
              />
            )}
          </div>

          {/* row 2 - room type, dates, purchase cost, cost to tp */}
          <div
            className={classNames('grid gap-20px', {
              'grid-cols-[1fr_1fr_110px_110px_24px]': !props.isTA,
              'grid-cols-[1fr_1fr_110px_110px]': props.isTA,
            })}
          >
            {/* accommodation name */}
            <div className="flex flex-col gap-20px">
              <label className="uppercase font-pt-sans text-xs text-gray-100 font-bold">Room Type</label>
              <HidingTextTooltip tooltipContent={accom.title} position="bottom" tooltipContentClassname="room-name">
                <AccommodationTopInput
                  placeholder="Room Name"
                  disabled={isEditable === false}
                  key={`Accommodation.items[${uuid}].title`}
                  className={classnames(
                    'name border text-base border-solid border-gray-40 p-2 w-full font-pt-sans font-bold min-h-[39px] max-h-[39px]',
                    {
                      'bg-gray-10': isEditable === false,
                    }
                  )}
                  type="text"
                  value={accom.title}
                  onChange={event => {
                    props.setHeadlineLineItemFieldAction(`Accommodation.items[${uuid}].title`, event.target.value);
                    dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                  }}
                />
              </HidingTextTooltip>
            </div>

            {/* dates and date/overlap warnings */}
            <div className="relative flex flex-col gap-20px">
              <label className="uppercase font-pt-sans text-xs text-gray-100 font-bold">Dates</label>
              <DatePickerStateProvider
                key={`accom-dates-${index}-${accom.title}-${accom.arrival}-${accom.departure}`}
                defaultSelectedDates={DateUtc.generateArrayOfDatesBetween(accom.arrival!, accom.departure!)}
                onDateChange={(e: string[]) => {
                  const start = e[0];
                  const end = e[e.length - 1];
                  props.setHeadlineLineItemFieldAction(`Accommodation.items[${uuid}].arrival`, start);
                  props.setHeadlineLineItemFieldAction(`Accommodation.items[${uuid}].departure`, end);
                  setIsCalendarDatesEdited(true);
                  setHasDismissedOverlapWarning(false);
                  dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                }}
                render={(params: IDatePickerSateParams) => (
                  <StyledDateRangeInput
                    disabled={isEditable === false}
                    datePickerLeft
                    className={classnames('cursor-pointer text-base w-full font-pt-sans font-bold', {
                      'bg-gray-10': isEditable === false,
                    })}
                    displayString={params.displayString}
                    currentDate={params.datePickerCurrentDate}
                    totalNights={params.totalNights}
                    selectedDates={params.selectedDates}
                    onDayClick={params.handleDayClick}
                    onDayMouseOver={params.handleDateMouseOver}
                    showDatePicker={params.showDatePicker}
                    onNextClick={params.incrementDate}
                    onPrevClick={params.decrementDate}
                    onMouseDown={params.toggleDatePicker}
                    onClickOutside={params.hideDatePicker}
                    enablePastDates
                  />
                )}
              />
              {isCalendarDatesEdited && (
                <div
                  className={classNames(
                    'absolute top-[74px] w-[142px] rounded border border-solid bg-yellow-advert-light border-yellow-advert p-2 text-black text-13px',
                    {
                      'left-[175px]': hasOverlappingDates && !hasDismissedOverlapWarning,
                      'left-0': !(hasOverlappingDates && !hasDismissedOverlapWarning),
                    }
                  )}
                >
                  <span>Dates modified. Please check the rest of the items.</span>
                  <button
                    onClick={() => {
                      setIsCalendarDatesEdited(false);
                    }}
                    className="cursor-pointer bg-transparent absolute border-none top-2 right-1"
                  >
                    <SvgIcon
                      IconComponent={CloseIcon}
                      defaultFill={theme.colors['black']}
                      hoverFill={theme.colors['black']}
                      activeFill={theme.colors['black']}
                      width="12px"
                      height="12px"
                    />
                  </button>
                </div>
              )}

              {hasOverlappingDates && !hasDismissedOverlapWarning && (
                <div
                  className={classNames(
                    'absolute left-[50px] top-[-23px] rounded border border-solid bg-yellow-advert-light border-yellow-advert p-2 text-black text-13px',
                    {
                      'w-[329px]': props.isTA,
                      'w-[308px]': !props.isTA,
                    }
                  )}
                >
                  <span>Rooms seem to overlap. Check if you need to add guests.</span>
                  <button
                    onClick={() => {
                      setHasDismissedOverlapWarning(true);
                    }}
                    className="cursor-pointer bg-transparent absolute border-none top-2 right-1"
                  >
                    <SvgIcon
                      IconComponent={CloseIcon}
                      defaultFill={theme.colors['black']}
                      hoverFill={theme.colors['black']}
                      activeFill={theme.colors['black']}
                      width="12px"
                      height="12px"
                    />
                  </button>
                </div>
              )}
            </div>

            {/* purchase cost */}
            {isPurchaseCostVisible && (
              <div className="flex flex-col gap-20px justify-self-end">
                <label className="uppercase font-pt-sans text-xs text-gray-100 font-bold self-end">Purchase Cost</label>
                <PriceAsCentsInput
                  disabled={isPurchaseCostDisabled}
                  key={`Accommodation.items[${uuid}].purchaseCostCents`}
                  className={classnames(
                    'purchase-cost bg-blue-10 border text-base border-solid border-gray-40 p-2 font-pt-sans font-bold text-right min-h-[39px] max-h-[39px] w-110px self-end',
                    {
                      'bg-gray-10': props.isPurchaseCostEditable === false,
                    }
                  )}
                  cents={accom.purchaseCostCents || 0}
                  onBlurUpdate={cents => {
                    props.setHeadlineLineItemFieldAction(`Accommodation.items[${uuid}].purchaseCostCents`, cents);
                  }}
                  onChange={() => {
                    dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                  }}
                />
              </div>
            )}
            {!isPurchaseCostVisible && <div>{/* deliberately blank for layout */}</div>}

            {/* cost to tp */}
            <div className="flex flex-col gap-20px justify-self-end">
              <label className="uppercase font-pt-sans text-xs text-gray-100 font-bold self-end">Cost to TP</label>
              <PriceAsCentsInput
                disabled={!isSalesCostEditable}
                key={`Accommodation.items[${uuid}].saleCostCents`}
                className={classnames(
                  'cost-to-ta border text-base border-solid border-gray-40 p-2 font-pt-sans font-bold text-right min-h-[39px] max-h-[39px] w-110px self-end',
                  {
                    'bg-gray-10': !isSalesCostEditable,
                  }
                )}
                cents={accom.saleCostCents || 0}
                onBlurUpdate={val => {
                  props.setHeadlineLineItemFieldAction(`Accommodation.items[${uuid}].saleCostCents`, val);
                }}
                onChange={() => {
                  dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                }}
              />
            </div>
          </div>

          {/* row 3 - tertiary text (dates and offers) */}
          <div
            className={classNames('mt-5 grid gap-20px', {
              'grid-cols-[1fr_110px_110px_24px]': !props.isTA,
              'grid-cols-[1fr_110px_110px]': props.isTA,
            })}
          >
            <HidingTextTooltip
              tooltipContent={accom.tertiaryText}
              position="bottom"
              tooltipContentClassname="tertiary-text"
            >
              <AccommodationTopInput
                placeholder="Dates and Offers"
                disabled={isEditable === false}
                key={`Accommodation.items[${uuid}].tertiaryText`}
                className={classnames('details border text-base border-solid border-gray-40 p-2 w-full font-pt-sans', {
                  'bg-gray-10': isEditable === false,
                })}
                type="text"
                value={accom.tertiaryText || ''}
                onChange={event => {
                  props.setHeadlineLineItemFieldAction(`Accommodation.items[${uuid}].tertiaryText`, event.target.value);
                  dispatch(BreakdownActions.setIsAccommodationModifiedAction(true));
                }}
              />
            </HidingTextTooltip>
          </div>

          {/* the meal plans block */}
          <ProductsBlock
            titleJSX={<h2 className="text-17px text-left m-0">Meal Plans</h2>}
            key={`Accommodation-${index}-Meal Plan`}
            pathPrefix={`Accommodation.items[${uuid}]`}
            productType={'Meal Plan'}
            className={classNames('meal-plan mt-4', {
              'w-[calc(100%-69px)]': showRightButtonGroup,
              'w-full': !showRightButtonGroup,
            })}
            lineItems={accom['Meal Plan'].items}
            titlePlaceholder={'Meal Plan'}
            tertiaryTextPlaceholder={'Guests'}
            setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
            removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
            subTotalCents={accom['Meal Plan'].subtotalCents}
            bookingCurrencySymbol={props.bookingCurrencySymbol}
            isPurchaseCostEditable={props.isPurchaseCostEditable}
            isTa={props.isTA}
            isSR={props.isSR}
            isAdmin={props.isAdmin}
            isFinanceUser={props.isFinanceUser}
            isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
            isSales={props.isSales}
            cta={
              isEditable ? (
                <button
                  className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                  onClick={() => {
                    props.addHeadlineLineItemAction(`Accommodation.items[${uuid}].Meal Plan.items`);
                  }}
                >
                  Add Meal Plan
                </button>
              ) : (
                undefined
              )
            }
            isEditable={subProductsAreEditable}
            isLatestVersion={props.isLatestVersion}
            isSalesCostEditable={isSubProductsSalesCostEditable}
            isLiveRate={props.isLiveRate}
            isAccommodationSubBlock={true}
          />

          {/* the live rate cancellation policies, if we have them */}
          {!isNilOrEmpty(accom.externalBooking) &&
            accom.externalBooking !== null &&
            accom.externalBooking !== undefined && (
              <div
                className={classNames('grid gap-20px items-center', {
                  'grid-cols-[1fr_24px]': !props.isTA,
                  'grid-cols-[1fr]': props.isTA,
                })}
              >
                <div>
                  {props.isLatestVersion && (
                    <div className="flex flex-row justify-between mt-2">
                      {networkData.getLiveAccommodationCancellationDataLoad === ENetworkRequestStatus.SUCCESS &&
                        !props.isTA &&
                        accom.externalBooking.status !== 'enquiry' && (
                          <FluidButton
                            className="live-accommodation-request-cancellation"
                            type="secondary"
                            disabled={bookingStatus !== EBookingStatus.CONFIRMED}
                            onClick={() => {
                              dispatch(BreakdownActions.resetLiveAccommodationCancellationModalAction());
                              dispatch(BreakdownActions.addLiveAccommodationCancellationModalAccomAction(accom));
                              dispatch(BreakdownActions.setLiveAccommodationCancellationModalIsOpenAction(true));
                            }}
                          >
                            Request Cancellation
                          </FluidButton>
                        )}
                      {(networkData.getLiveAccommodationCancellationDataLoad !== ENetworkRequestStatus.SUCCESS ||
                        props.isTA ||
                        accom.externalBooking.status === 'enquiry') && <div>{/* deliberately blank for layout */}</div>}
                      <button
                        onClick={() => {
                          setIsCancellationPoliciesOpen(i => !i);
                        }}
                        className="bg-white border-none text-brown-100 cursor-pointer hover:text-brown-130"
                      >
                        <span className="flex flex-row items-center">
                          {networkData.getLiveAccommodationCancellationDataLoad === ENetworkRequestStatus.PENDING && (
                            <span className="underline text-15px leading-20px">Loading Cancellation Details</span>
                          )}
                          {networkData.getLiveAccommodationCancellationDataLoad !== ENetworkRequestStatus.PENDING && (
                            <React.Fragment>
                              <span className="underline text-15px leading-20px">See Cancellation Details</span>
                              <SvgIcon
                                IconComponent={isCancellationPoliciesOpen ? ChevronUpIcon : ChevronDownIcon}
                                className="w-4 fill-transparent stroke-current stroke-2 relative ml-2 inline"
                              />
                            </React.Fragment>
                          )}
                        </span>
                      </button>
                    </div>
                  )}

                  {isCancellationPoliciesOpen && (
                    <div className="bg-ivory font-pt-sans border border-solid border-gray-20 mt-5 py-10px px-15px">
                      <span className="block text-15px leading-21px font-bold">Cancellation Cost</span>
                      {networkData.getLiveAccommodationCancellationDataLoad === ENetworkRequestStatus.ERROR && (
                        <span className="text-15px leading-20px text-red-95">Error loading cancellation details</span>
                      )}
                      {!!liveAccommodationCancellationPolicies.length && (
                        <>
                          <ul>
                            {liveAccommodationCancellationPolicies.map(cp => {
                              return <li className="text-15px leading-21px">{cp} *</li>;
                            })}
                          </ul>
                          <span className="footnote-on-cancellation-policies block text-sm leading-20px text-black-true">
                            ( * ) Time at destination
                          </span>
                        </>
                      )}
                      {!liveAccommodationCancellationPolicies.length && (
                        <span className="text-15px leading-21px inline-block mt-1">
                          Please, confirm the cancellation policy of this rate with your sales contact.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* the supplements */}
          <ProductsBlock
            titleJSX={<h2 className="text-17px text-left m-0">Supplements</h2>}
            key={`Accommodation-${index}-Supplement`}
            pathPrefix={`Accommodation.items[${uuid}]`}
            productType={'Supplement'}
            className={classNames('mt-4', {
              'w-[calc(100%-69px)]': showRightButtonGroup,
              'w-full': !showRightButtonGroup,
            })}
            lineItems={accom.Supplement.items}
            setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
            removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
            subTotalCents={accom.Supplement.subtotalCents}
            bookingCurrencySymbol={props.bookingCurrencySymbol}
            isPurchaseCostEditable={props.isPurchaseCostEditable}
            isTa={props.isTA}
            isSR={props.isSR}
            isAdmin={props.isAdmin}
            isFinanceUser={props.isFinanceUser}
            isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
            isSales={props.isSales}
            cta={
              props.isLatestVersion && isInternalUser ? (
                <button
                  className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                  onClick={() => {
                    dispatch(
                      BreakdownActions.addHeadlineLineItemAction(
                        `Accommodation.items[${uuid}].Supplement.items`,
                        undefined,
                        undefined,
                        undefined,
                        undefined,
                        true // force this item to be editable as it was manually added
                      )
                    );
                  }}
                >
                  Add
                </button>
              ) : (
                undefined
              )
            }
            isEditable={subProductsAreEditable}
            isLatestVersion={props.isLatestVersion}
            isSalesCostEditable={isSubProductsSalesCostEditable}
            isLiveRate={props.isLiveRate}
            isAccommodationSubBlock={true}
          />

          {/* the additional hotel charges */}
          {!props.isOldVersion && (
            <ProductsBlock
              titleJSX={<h2 className="text-17px text-left m-0">Additional Hotel Services</h2>}
              key={`Accommodation-${index}-AdditionalHotelCharges`}
              pathPrefix={`Accommodation.items[${uuid}]`}
              productType={ProductTypes.ADDITIONAL_HOTEL_CHARGES}
              className={classNames('mt-4', {
                'w-[calc(100%-69px)]': showRightButtonGroup,
                'w-full': !showRightButtonGroup,
              })}
              lineItems={accom.AdditionalHotelCharges.items}
              setHeadlineLineItemFieldAction={props.setHeadlineLineItemFieldAction}
              removeHeadlineLineItemAction={props.removeHeadlineLineItemAction}
              subTotalCents={accom.AdditionalHotelCharges.subtotalCents}
              bookingCurrencySymbol={props.bookingCurrencySymbol}
              isPurchaseCostEditable={props.isPurchaseCostEditable}
              isTa={props.isTA}
              isSR={props.isSR}
              isAdmin={props.isAdmin}
              isFinanceUser={props.isFinanceUser}
              isFinanceWithCompletedStatus={props.isFinanceWithCompletedStatus}
              isSales={props.isSales}
              cta={
                props.isLatestVersion && isInternalUser ? (
                  <button
                    className="add border border-solid border-brown-80 hover:bg-brown-20 bg-white cursor-pointer font-bold text-sm p-2 rounded text-brown-80 font-pt-sans"
                    onClick={() => {
                      dispatch(
                        BreakdownActions.addHeadlineLineItemAction(
                          `Accommodation.items[${uuid}].AdditionalHotelCharges.items`,
                          undefined,
                          undefined,
                          undefined,
                          undefined,
                          true // force this item to be editable as it was manually added
                        )
                      );
                    }}
                  >
                    Add
                  </button>
                ) : (
                  undefined
                )
              }
              isEditable={subProductsAreEditable}
              isLatestVersion={props.isLatestVersion}
              isSalesCostEditable={isSubProductsSalesCostEditable}
              isLiveRate={props.isLiveRate}
              isAccommodationSubBlock={true}
            />
          )}

          {/* the subtotals */}
          <SubtotalsRow
            isTa={props.isTA}
            subTotalPurchaseCents={purchaseCostSubtotalCents}
            subTotalSalesCents={saleCostSubtotalCents}
            bookingCurrencySymbol={props.bookingCurrencySymbol}
          />
        </div>
      </div>

      <AccommodationBlockBookLiveModal
        bookingCurrencySymbol={props.bookingCurrencySymbol}
        handleClose={bookLiveAccommodationOps.modalOps.handleClose}
        handleConfirm={bookLiveAccommodationOps.modalOps.handleConfirm}
        isOpen={bookLiveAccommodationOps.modalOps.isOpen}
        modalData={bookLiveAccommodationOps.modalOps.modalData}
      />
    </React.Fragment>
  );
};
